import qs from 'qs';
import { addDays, addMonths, differenceInDays } from 'date-fns';
import { IRoom, ISuggestionList, ISuggestion, IResort } from '../types';
import { getLocale } from '../../utils/pathHelper';
import { parseDate } from '../../utils/dateHelper';
import t from '../locales';

export const stringifyParams = (params: any) => {
  // 既存の宿実装に合わせるために children を追加
  // 宿予約等には必要ないが、宿詳細でのプラン検索UIに使用されています。
  const formattedRooms = (params.rooms || []).map((room: IRoom) => {
    return { ...room, children: (room.child_ages || []).length };
  });
  return qs.stringify({ ...params, rooms: formattedRooms }, { arrayFormat: 'brackets' });
};

export const toNumber = (entry: any, defaultValue: number) => {
  return entry == null || Number.isNaN(Number(entry)) ? defaultValue : Number(entry);
}

export const countPeople = (rooms: IRoom[]) => {
  let adultCount = 0;
  let childCount = 0;
  if (rooms) {
    for (let i = 0; i < rooms.length; i += 1) {
      adultCount += Number(rooms[i].adults);
      childCount += rooms[i].child_ages ? Number(rooms[i].child_ages.length) : 0;
    }
  }
  return { adultCount, childCount, total: adultCount + childCount };
}

export const countNights = (checkin_date: Date|string, checkout_date: Date|string): number|null => {
  const checkin = parseDate(checkin_date);
  const checkout = parseDate(checkout_date);
  if (!checkin || !checkout) { return null; }
  return differenceInDays(checkout, checkin);
}

export const defaultSuggestion = (q: ISuggestion|null) => {
  return (q && q.type && q.label) ? q : null
}

export const defaultRooms = (rooms: IRoom[]|null = null): IRoom[] => {
  if (rooms == null) {
    return [{ adults: 2, child_ages: [] }];
  } else {
    return rooms.map((room) => (
      {
        adults: toNumber(room.adults, 2),
        child_ages: room.child_ages == null || !Array.isArray(room.child_ages) ? [] :
                    (room.child_ages || []).map(age => toNumber(age, 0)),
      }
    ));
  }
}

export const defaultSuggestionList: ISuggestionList = {
  yado: [],
  resort: [],
  prefecture: [],
  area: [],
};

export const fetchYadoListPrices = (
  params: { checkin_date: string, checkout_date: string, rooms: IRoom[], yado_ids: string[] },
  onComplete: (data: any) => void
  ) => {
  fetch(`${process.env.YADO_ENDPOINT}/api/v1/yados?${stringifyParams(params)}`, {
    method: 'GET',
    headers: {
      'X-CLIENT-TYPE': 'web',
      'X-DEVICE-OS': 'web',
      'Accept-Language': getLocale({ underscore: false }),
    },
  })
  .then(data => data.json())
  .then((json) => {
    onComplete(json);
  })
  .catch((error) => {
    console.error(error);
  });
}

export const resortSectionTitle = (resorts: IResort[]) => {
  const lastResort = resorts[resorts.length - 1];
  if (lastResort == null) { return null; }
  const methodName = t[lastResort.access_method] ? t[lastResort.access_method]() : null;
  if (!methodName || !lastResort.access_minutes) { return null; }
  return lastResort.access_method === 'adjacent' ? methodName :
    `${methodName} ${t.withinMinutes({ minutes: lastResort.access_minutes })}`;
}

export const sortResorts = (resorts: IResort[]): { adjacent: IResort[], walk: IResort[], shuttleBus: IResort[] } => {
  const compare = (a: IResort, b: IResort) => {
    let comparison = 0;
    if (a.access_minutes && b.access_minutes && a.access_minutes > b.access_minutes) {
      comparison = 1;
    } else if (a.access_minutes && b.access_minutes && a.access_minutes < b.access_minutes) {
      comparison = -1;
    }
    return comparison;
  }
  const result = { adjacent: [], walk: [], shuttleBus: [] };
  if (resorts && (resorts || []).length > 0) {
    result.adjacent   = resorts.filter(resort => resort.access_method === 'adjacent').sort(compare);
    result.walk       = resorts.filter(resort => resort.access_method === 'walk').sort(compare);
    result.shuttleBus = resorts.filter(resort => resort.access_method === 'shuttle_bus').sort(compare);
  }
  return result;
}
